import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CodeIcon from '@mui/icons-material/Code';

import { Box, Button, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';

import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';

import { NoteSelectionActionPopup } from '../NoteSelectionActionPopup';

import {

    DEFAULT_TXT_SPLIT_PANE_HEIGHT,

    createTxtSplitPaneSx,

    txtSplitColumnSx,

    txtSplitHeaderSx,

    txtSplitHtmlFieldSx,

    txtSplitHtmlSourceStackSx,

} from './txtEditorStyles';

import { TxtSplitEditorHandle, useTxtSplitEditor, type UseTxtSplitEditorOptions } from './useTxtSplitEditor';



export type TxtSplitEditorProps = UseTxtSplitEditorOptions & {

    paneHeight?: number;

    layout?: 'row' | 'column' | 'auto';

    previewLabel?: string;

    htmlSourceLabel?: string;

    htmlSourcePlaceholder?: string;

    htmlSourceToggleLabel?: string;

    showHtmlSourceToggle?: boolean;

    showInsertTimestampButton?: boolean;

    insertTimestampButtonLabel?: string;

};



export const TxtSplitEditor = forwardRef<TxtSplitEditorHandle, TxtSplitEditorProps>(function TxtSplitEditor(

    {

        paneHeight = DEFAULT_TXT_SPLIT_PANE_HEIGHT,

        layout = 'auto',

        previewLabel = 'Preview (editable)',

        htmlSourceLabel = 'HTML source',

        htmlSourcePlaceholder = 'HTML source…',

        htmlSourceToggleLabel = 'HTML source',

        showHtmlSourceToggle = true,

        showInsertTimestampButton = false,

        insertTimestampButtonLabel = 'Alt+Q',

        enableTimestampShortcut,

        ...editorOptions

    },

    ref,

) {

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const resolvedLayout = layout === 'auto' ? (isMobile ? 'column' : 'row') : layout;

    const splitPaneSx = useMemo(() => createTxtSplitPaneSx(paneHeight), [paneHeight]);



    const {

        showHtmlSource,

        handleToggleHtmlSource,

        insertTimestamp,

        txtPreviewRef,

        previewContentEditableRef,

        descriptionTextareaRef,

        txtPreviewSelection,

        hideTxtPreviewSelection,

        htmlSourceStackRef,

        htmlSourceScrollTop,

        showHtmlSourceSyncOverlay,

        htmlSourceSelectionBand,

        htmlSourceTextareaInset,

        updateTxtEditorSyncHighlight,

        handleTextareaFocus,

        handleHtmlSourceScroll,

        updateTxtPreviewSelection,

        handlePreviewDoubleClick,

        handlePreviewMouseDown,

        handlePreviewEditableInput,

        handlePreviewCut,

        handlePreviewEditableFocus,

        handlePreviewEditableBlur,

        handlePreviewKeyDown,

        handlePreviewEditablePaste,

        handleTextareaBlur,

        handleTextareaPaste,

        handleImperative,

    } = useTxtSplitEditor({
        ...editorOptions,
        enableTimestampShortcut: enableTimestampShortcut ?? showInsertTimestampButton,
    });



    useImperativeHandle(ref, () => handleImperative, [handleImperative]);



    useEffect(() => {

        if (editorOptions.disabled) {

            hideTxtPreviewSelection();

        }

    }, [editorOptions.disabled, hideTxtPreviewSelection]);



    return (

        <Box

            sx={{

                display: 'flex',

                flexDirection: resolvedLayout === 'column' ? 'column' : 'row',

                gap: 2,

                alignItems: 'stretch',

            }}

        >

            <Box sx={txtSplitColumnSx}>

                <Box sx={txtSplitHeaderSx}>

                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>

                        {previewLabel}

                    </Typography>

                    {showHtmlSourceToggle ? (

                        <Tooltip title={showHtmlSource ? 'Hide HTML source' : 'Show HTML source'}>

                            <Button

                                size="small"

                                variant={showHtmlSource ? 'contained' : 'outlined'}

                                startIcon={<CodeIcon fontSize="small" />}

                                onClick={handleToggleHtmlSource}

                                sx={{ textTransform: 'none' }}

                            >

                                {htmlSourceToggleLabel}

                            </Button>

                        </Tooltip>

                    ) : null}

                    {showInsertTimestampButton ? (

                        <Tooltip title="Insert timestamp (Alt+Q)">

                            <Button

                                size="small"

                                variant="outlined"

                                startIcon={<AccessTimeIcon fontSize="small" />}

                                onClick={insertTimestamp}

                                disabled={editorOptions.disabled}

                                sx={{ textTransform: 'none' }}

                            >

                                {insertTimestampButtonLabel}

                            </Button>

                        </Tooltip>

                    ) : null}

                </Box>

                <Box sx={splitPaneSx}>

                    <Box

                        ref={txtPreviewRef}

                        sx={{

                            flex: 1,

                            minHeight: 0,

                            overflowY: 'auto',

                            p: 1.5,

                            border: '1px solid',

                            borderColor: 'divider',

                            borderRadius: 1,

                            bgcolor: 'background.paper',

                            position: 'relative',

                        }}

                    >

                        {txtPreviewSelection && (

                            <NoteSelectionActionPopup

                                selection={txtPreviewSelection}

                                onClose={hideTxtPreviewSelection}

                            />

                        )}

                        <Box

                            ref={previewContentEditableRef}

                            data-txt-preview-content

                            contentEditable={!editorOptions.disabled}

                            suppressContentEditableWarning

                            spellCheck={false}

                            autoCorrect="off"

                            autoCapitalize="off"

                            data-gramm="false"

                            data-gramm_editor="false"

                            data-enable-grammarly="false"

                            onInput={handlePreviewEditableInput}

                            onCut={handlePreviewCut}

                            onFocus={handlePreviewEditableFocus}

                            onBlur={handlePreviewEditableBlur}

                            onKeyDown={handlePreviewKeyDown}

                            onPaste={handlePreviewEditablePaste}

                            onMouseDown={handlePreviewMouseDown}

                            onMouseUp={updateTxtPreviewSelection}

                            onDoubleClick={handlePreviewDoubleClick}

                            onKeyUp={updateTxtPreviewSelection}

                            sx={{

                                outline: 'none',

                                minHeight: '100%',

                                cursor: 'text',

                                '&:focus': {

                                    outline: '2px solid',

                                    outlineColor: 'primary.light',

                                    borderRadius: 0.5,

                                },

                                '& a': { color: 'primary.main' },

                                '& iframe': { maxWidth: '100%' },

                            }}

                        />

                    </Box>

                </Box>

            </Box>

            {showHtmlSource && (

                <Box sx={txtSplitColumnSx}>

                    <Box sx={txtSplitHeaderSx}>

                        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>

                            {htmlSourceLabel}

                        </Typography>

                    </Box>

                    <Box sx={splitPaneSx}>

                        <Box ref={htmlSourceStackRef} sx={txtSplitHtmlSourceStackSx}>

                            <TextField

                                fullWidth

                                multiline

                                value={editorOptions.value || ''}

                                onChange={(e) => editorOptions.onChange(e.target.value)}

                                onBlur={handleTextareaBlur}

                                onFocus={handleTextareaFocus}

                                onSelect={updateTxtEditorSyncHighlight}

                                onMouseUp={updateTxtEditorSyncHighlight}

                                onKeyUp={(e) => {

                                    if (

                                        e.shiftKey ||

                                        e.key.startsWith('Arrow') ||

                                        e.key === 'Home' ||

                                        e.key === 'End' ||

                                        e.key === 'PageUp' ||

                                        e.key === 'PageDown'

                                    ) {

                                        updateTxtEditorSyncHighlight();

                                    }

                                }}

                                onPaste={handleTextareaPaste}

                                inputRef={descriptionTextareaRef}

                                disabled={editorOptions.disabled}

                                placeholder={htmlSourcePlaceholder}

                                slotProps={{

                                    htmlInput: {

                                        'data-txt-html-source': '1',

                                        spellCheck: false,

                                        autoComplete: 'off',

                                        autoCorrect: 'off',

                                        autoCapitalize: 'off',

                                        'data-gramm': 'false',

                                        'data-gramm_editor': 'false',

                                        'data-enable-grammarly': 'false',

                                        onScroll: handleHtmlSourceScroll,

                                    },

                                }}

                                sx={{

                                    ...txtSplitHtmlFieldSx,

                                    gridArea: '1 / 1',

                                    zIndex: 1,

                                }}

                            />

                            {showHtmlSourceSyncOverlay && htmlSourceTextareaInset && htmlSourceSelectionBand && (

                                <Box

                                    aria-hidden

                                    sx={{

                                        position: 'absolute',

                                        top: htmlSourceTextareaInset.top,

                                        left: htmlSourceTextareaInset.left,

                                        width: htmlSourceTextareaInset.width,

                                        height: htmlSourceTextareaInset.height,

                                        overflow: 'hidden',

                                        pointerEvents: 'none',

                                        zIndex: 2,

                                    }}

                                >

                                    <Box

                                        sx={{

                                            position: 'absolute',

                                            top: htmlSourceSelectionBand.top - htmlSourceScrollTop,

                                            left: 0,

                                            right: 0,

                                            height: htmlSourceSelectionBand.height,

                                            bgcolor: 'rgba(34, 211, 238, 0.42)',

                                            borderRadius: '2px',

                                        }}

                                    />

                                </Box>

                            )}

                        </Box>

                    </Box>

                </Box>

            )}

        </Box>

    );

});


